/**
 * Leads list picker section for prepared drop modals.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, Filter, Search, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';
import { Input } from '@/components/ui';
import { LeadCountBadge, LeadsListTags, NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import type { LeadsListOption } from '../types';

/** Map of leadsListWiserId -> amount used for the work date. */
type UsageMap = Record<string, number>;

interface LeadsSectionProps {
  leadsLists: LeadsListOption[];
  selectedId: number | '';
  onSelect: (id: number | '') => void;
  search: string;
  onSearch: (v: string) => void;
  showLeads: boolean;
  onToggleShow: () => void;
  allowUnchanged?: boolean;
  showSelectedBadge?: boolean;
  /** Per-list usage for remaining calculation; omit when not available. */
  usageByListId?: UsageMap;
  /** Custom tag colors from list tag rules */
  tagColors?: Record<string, string>;
  /** When true, shows LoadingSpinner in tag filter (tags loading). */
  tagsLoading?: boolean;
  /** All available tag options from API. When provided, used for dropdown instead of deriving from leads. */
  tagFilterOptions?: string[];
}

export const LeadsSection: FC<LeadsSectionProps> = ({
  leadsLists,
  selectedId,
  onSelect,
  search,
  onSearch,
  showLeads,
  onToggleShow,
  allowUnchanged = false,
  showSelectedBadge = true,
  usageByListId,
  tagColors,
  tagsLoading,
  tagFilterOptions: tagFilterOptionsProp,
}) => {
  const [leadTagFilter, setLeadTagFilter] = useState('');

  const availableTags = useMemo(() => {
    if (tagFilterOptionsProp && tagFilterOptionsProp.length > 0) {
      return tagFilterOptionsProp;
    }
    const set = new Set<string>(['New', 'Unused']);
    for (const l of leadsLists) {
      const tags = l.performanceTags ?? [];
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [leadsLists, tagFilterOptionsProp]);

  const filtered = useMemo(() => {
    let list = leadsLists.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()));
    if (leadTagFilter) {
      if (leadTagFilter === '(none)') {
        list = list.filter((l) => !(l.performanceTags?.length));
      } else {
        const q = leadTagFilter.toLowerCase();
        list = list.filter((l) =>
          l.performanceTags?.some((t) => t.toLowerCase() === q),
        );
      }
    }
    return [...list].sort((a, b) => {
      const aSel = a.id === selectedId;
      const bSel = b.id === selectedId;
      return aSel === bSel ? 0 : aSel ? -1 : 1;
    });
  }, [leadsLists, search, leadTagFilter, selectedId]);

  const handleSelect = (id: number) => {
    if (allowUnchanged && selectedId === id) {
      onSelect('');
    } else {
      onSelect(id);
    }
  };

  return (
    <section className="flex flex-1 flex-col space-y-2 overflow-hidden">
      <button
        type="button"
        onClick={onToggleShow}
        className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          Leads list
          {showSelectedBadge && (typeof selectedId === 'number' ? selectedId > 0 : selectedId !== '') && (
            <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
              selected
            </span>
          )}
        </h4>
        <ChevronDown className={cn('h-4 w-4 transition-transform', showLeads && 'rotate-180')} />
      </button>
      {showLeads && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="border-b border-border bg-background p-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="Search leads lists..."
                  className="h-9 pl-9"
                />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              {tagsLoading ? (
                <span className="flex h-8 items-center gap-1.5 text-xs text-muted-foreground">
                  <LoadingSpinner size="xs" />
                  Tags…
                </span>
              ) : (
              <TagFilterDropdown
                  value={leadTagFilter === '(none)' ? NO_TAGS_VALUE : leadTagFilter}
                  options={availableTags}
                  tagColors={tagColors}
                  displayLabel={
                    leadTagFilter === ''
                      ? 'All tags'
                      : leadTagFilter === '(none)'
                        ? 'No tags'
                        : leadTagFilter
                  }
                  onSelect={(v) =>
                    setLeadTagFilter(v === '' ? '' : v === NO_TAGS_VALUE ? '(none)' : v)
                  }
                  placeholder="All tags"
                  compact
                />
              )}
              </div>
            </div>
          </div>
          <div className="leads-grid-container min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-muted/5 p-4">
            <div className="leads-grid w-full">
              {allowUnchanged && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect('')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect('');
                    }
                  }}
                  className={cn(
                    'group relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-lg border p-3 shadow-sm transition-colors hover:border-primary/50',
                    selectedId === ''
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border bg-card',
                  )}
                >
                  <span className="text-sm italic text-muted-foreground">— Leave unchanged —</span>
                </div>
              )}
              {filtered.length === 0 && !allowUnchanged ? (
                <div className="col-span-2 py-10 text-center text-sm text-muted-foreground">
                  {search || leadTagFilter
                    ? 'No leads lists match your filters.'
                    : 'No leads lists.'}
                </div>
              ) : null}
              {filtered.map((lead) => {
                const isSelected = selectedId === lead.id;
                return (
                  <div
                    key={lead.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(lead.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(lead.id);
                      }
                    }}
                    className={cn(
                      'group relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-lg border p-3 shadow-sm transition-colors hover:border-primary/50',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border bg-card',
                    )}
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span
                        className={cn(
                          'min-w-0 truncate text-sm font-medium leading-tight',
                          isSelected ? 'text-primary' : 'text-foreground',
                        )}
                      >
                        {lead.name}
                      </span>
                      <LeadsListTags
                        performanceTags={lead.performanceTags}
                        tagColors={tagColors}
                      />
                    </div>
                    <LeadCountBadge
                      count={lead.count}
                      usage={
                        (lead as { usageToday?: number }).usageToday ??
                        usageByListId?.[String(lead.id)]
                      }
                      remaining={(lead as { remaining?: number | null }).remaining}
                      compact
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
